import { Injectable } from '@angular/core';
import { HttpEvent, HttpHandler, HttpInterceptor, HttpRequest, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { environment } from '../../../environments/environment';
import { AppSettings } from '../PRE-LOAD/app-settings';
import { delay } from 'rxjs/operators';
import { Reservation } from '../types/booking.type';

interface InterceptorConfig {
    path: string;
    callback: any;
    status: number;
    method: string;
}

interface Day {
    availableDate: Date;
    times: TimeSlot[];
}

interface TimeSlot {
    startTime: Date;
    endTime: Date;
    isAvailable: boolean;
}

@Injectable()
export class HttpMockBackendRequestInterceptor implements HttpInterceptor {
    private days: Day[];
    private interceptors: InterceptorConfig[] = [
        {
            path: `api/retailers/${this.appSettings.externalReferenceId}/models`,
            callback: HttpMockBackendRequestInterceptor.getModels,
            status: 200,
            method: 'GET'
        },
        {
            path: 'api/availability',
            callback: this.getAvailability,
            status: 200,
            method: 'GET'
        },
        {
            path: 'api/bookings',
            callback: HttpMockBackendRequestInterceptor.createReservation,
            status: 200,
            method: 'POST'
        },
        {
            path: 'api/bookings',
            callback: HttpMockBackendRequestInterceptor.getReservation,
            status: 200,
            method: 'GET'
        },
        {
            path: 'api/bookings',
            callback: HttpMockBackendRequestInterceptor.patchReservation,
            status: 200,
            method: 'PATCH'
        }
    ];

    /**
     * @description Returns an array of mock model availability data.
     */
    private static getModels(): any[] {
        return [
            {
                id: 'b4e9a232-f0fa-6448-37dd-3854cc83f641',
                modelTreeRef: 'B0#F40-SH'
            },
            {
                id: 'b4e9a232-f0fa-6448-37dd-3854cc8409a9',
                modelTreeRef: 'B0#G20-LI'
            },
            {
                id: 'b4e9a232-f0fa-6448-37dd-3854cc841dc6',
                modelTreeRef: 'B0#F32-CP'
            },
            {
                id: 'b4e9a232-f0fa-6448-37dd-3854cc844145',
                modelTreeRef: 'B0#2C71'
            },
            {
                id: 'abc',
                modelTreeRef: 'MI#F60'
            }
        ];
    }

    /**
     * @description Returns a mock API response for creating a reservation.
     */
    private static createReservation() {
        return {
            id: 'abc123',
            start: new Date(),
            end: new Date(),
            modelTreeRef: 'B0#F40-SH',
            status: 'open'
        };
    }

    private static getReservation(): any {
        return {
            id: 'abc123',
            start: new Date(),
            end: new Date(),
            modelTreeReference: 'B0#F40-SH',
            status: 'open'
        };
    }

    private static patchReservation(): Reservation {
        return {
            id: 'abc123',
            start: new Date(),
            end: new Date(),
            modelTreeRef: 'B0#F40-SH',
            status: 'open'
        };
    }

    constructor(
        private appSettings: AppSettings
    ) {
    }

    intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
        if (environment.mock) {
            for (const interceptor of this.interceptors) {
                if (request.url.includes(interceptor.path) && request.method === interceptor.method) {
                    // Console logs for debugging and testing.
                    console.log('Intercepted http call: ' + request.url);
                    console.log(request);
                    // Fake a .5sec server response time with delay(500);
                    return of(new HttpResponse({status: interceptor.status, body: interceptor.callback.bind(this)()})).pipe(
                        delay(500)
                    );
                }
            }
            return next.handle(request);
        }
    }

    /**
     * @description Returns an array of Days with randomised timeslot availabilities.
     */
    private getAvailability(): Day[] {
        if (this.days) {
            return this.days;
        }
        // An increment counter.
        let i = 0;

        // Create an array of days and create the first day equal to today.
        const days: Day[] = [
            {
                availableDate: new Date(),
                times: []
            }
        ];

        const dayMilliseconds = 60 * 60 * 24 * 1000;
        // Iterate up to 30 and create a day by incrementing +1 day on the previous. Also generate
        // some time data.
        while (i < 30) {
            const date = new Date(days[i].availableDate.getTime() + dayMilliseconds);
            date.setHours(0, 0, 0, 0);
            days.push({
                availableDate: date,
                times: this.getTimes(date)
            });
            i = i + 1;
        }

        // Its unlikely that our random time generator will create days with no available timeslots,
        // so randomly choose days to set all times to not be available.
        days.forEach(day => {
            // Approx 1 in 5.
            if (Math.random() >= 0.8) {
                day.times.forEach(timeSlot => timeSlot.isAvailable = false);
            }
        });

        this.days = days;

        return days;
    }

    /**
     * @description Given a Date, return timeslots with random availabilities.
     */
    private getTimes(date: Date): TimeSlot[] {
        const startHours = [9, 10, 11, 12, 13, 14, 15, 16];
        const times = [];

        startHours.forEach(hour => {
            const hourDate = new Date(date);
            times.push({
                startTime: hourDate.setHours(hour),
                endTime: hourDate.setHours(hour + 1),
                available: Math.random() >= 0.5
            });
        });

        return times;
    }
}
